import { useEffect, useState } from "react";
import { Activity, ArrowRight, CheckCircle2, Hash, PhoneCall, RefreshCw, Route, Search, XCircle } from "lucide-react";
import { api, apiErrToStr } from "@/lib/api";

function StepIcon({ status }) {
  if (status === "ok") return <CheckCircle2 size={16} className="text-[color:var(--accent-green)]" />;
  if (status === "error") return <XCircle size={16} className="text-[color:var(--accent-red)]" />;
  return <Activity size={16} className="text-[color:var(--accent-amber)]" />;
}

export default function CallTest() {
  const [form, setForm] = useState({ direction: "outbound", origin: "", destination: "" });
  const [ramais, setRamais] = useState([]);
  const [dids, setDids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const loadHints = async () => {
    const [extensions, didRows] = await Promise.all([
      api.get("/ramais").then(({ data }) => data || []).catch(() => []),
      api.get("/dids").then(({ data }) => data || []).catch(() => []),
    ]);
    setRamais(extensions);
    setDids(didRows);
    setForm((current) => ({
      ...current,
      origin: current.origin || extensions[0]?.numero || "",
      destination: current.destination || "071999999999",
    }));
  };

  useEffect(() => {
    loadHints();
  }, []);

  const simulate = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/call-test/simulate", form);
      setResult(data);
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyDid = (numero) => setForm((current) => ({ ...current, direction: "inbound", destination: numero }));
  const applyRamal = (numero) => setForm((current) => ({ ...current, direction: "outbound", origin: numero }));

  return (
    <div className="space-y-5" data-testid="call-test-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Validacao de rota</div>
          <h1 className="text-2xl font-semibold tracking-tight">Teste automatico de chamada</h1>
          <p className="mt-1 text-sm text-[color:var(--text-secondary)]">Simule entrada e saida sem gerar uma chamada real.</p>
        </div>
        <button className="sbc-btn" onClick={loadHints}>
          <RefreshCw size={14} /> Atualizar listas
        </button>
      </div>

      <form className="sbc-card p-5 space-y-4" onSubmit={simulate}>
        <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
          <label>
            <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Direcao</span>
            <select className="sbc-input" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
              <option value="outbound">Saida</option>
              <option value="inbound">Entrada</option>
            </select>
          </label>
          <label>
            <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Origem</span>
            <input className="sbc-input font-mono" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} placeholder={form.direction === "outbound" ? "Ramal origem" : "Numero chamador"} />
          </label>
          <label>
            <span className="mb-1 block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Destino</span>
            <input className="sbc-input font-mono" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder={form.direction === "outbound" ? "071999999999" : "+5571..."} required />
          </label>
          <button className="sbc-btn sbc-btn-primary" disabled={loading}>
            {loading ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
            Simular
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-sm border border-[color:var(--border-default)] p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold"><PhoneCall size={14} /> Ramais</div>
            <div className="flex max-h-28 flex-wrap gap-2 overflow-auto">
              {ramais.slice(0, 24).map((row) => (
                <button key={row.id} type="button" className="sbc-btn !px-2 !py-1 text-xs" onClick={() => applyRamal(row.numero)}>
                  {row.numero}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-sm border border-[color:var(--border-default)] p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold"><Hash size={14} /> DIDs</div>
            <div className="flex max-h-28 flex-wrap gap-2 overflow-auto">
              {dids.slice(0, 24).map((row) => (
                <button key={row.id} type="button" className="sbc-btn !px-2 !py-1 text-xs" onClick={() => applyDid(row.numero)}>
                  {row.numero}
                </button>
              ))}
            </div>
          </div>
        </div>
      </form>

      {error && <div className="rounded-sm border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 text-xs font-mono text-[color:var(--accent-red)]">! {error}</div>}

      {result && (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.5fr)_360px]">
          <section className="sbc-card overflow-hidden">
            <div className="border-b border-[color:var(--border-default)] p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-semibold">Resultado da simulacao</h2>
                  <p className="mt-1 text-xs text-[color:var(--text-muted)]">{result.ok ? "Caminho encontrado" : "Caminho com bloqueio ou pendencia"}</p>
                </div>
                <span className={`sbc-tag ${result.ok ? "tag-online" : "tag-danger"}`}>{result.ok ? "OK" : "Revisar"}</span>
              </div>
            </div>
            <div className="divide-y divide-[color:var(--border-default)]">
              {(result.steps || []).map((step, index) => (
                <div key={`${step.title}-${index}`} className="flex items-start gap-3 px-4 py-3">
                  <StepIcon status={step.status} />
                  <div>
                    <div className="font-medium">{step.title}</div>
                    <div className="text-sm text-[color:var(--text-secondary)]">{step.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <aside className="space-y-4">
            <div className="sbc-card p-4">
              <div className="mb-3 flex items-center gap-2 font-semibold"><Route size={15} /> Resumo</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span>Direcao</span><strong>{result.direction === "inbound" ? "Entrada" : "Saida"}</strong></div>
                <div className="flex justify-between gap-3"><span>Destino normalizado</span><strong className="font-mono">{result.normalized_destination || "-"}</strong></div>
                <div className="flex justify-between gap-3"><span>CID</span><strong className="font-mono">{String(result.caller_id || "-").replace("+", "")}</strong></div>
                <div className="flex justify-between gap-3"><span>Fallbacks</span><strong>{(result.fallback_routes || []).length}</strong></div>
              </div>
            </div>
            <div className="sbc-card p-4">
              <div className="mb-3 font-semibold">Avisos</div>
              {!(result.warnings || []).length ? (
                <div className="text-xs text-[color:var(--text-muted)]">Nenhum aviso adicional.</div>
              ) : (
                <div className="space-y-2">
                  {result.warnings.map((warning, index) => (
                    <div key={index} className="rounded-sm border border-[rgba(245,158,11,0.35)] bg-[rgba(245,158,11,0.08)] px-3 py-2 text-xs">
                      {warning}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
